import React from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Tag, Typography } from 'antd';
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkMath from 'remark-math';
import 'katex/dist/katex.min.css';

const PILLAR_TWO_OVERVIEW = String.raw`
Pillar 2 is **Expression Quality**. It asks whether the user turned a market
view into a sensible, correctly sized, and risk-controlled portfolio.

For each period, the assessment compares the user's saved positions with the benchmark's
**optimal portfolio**: the target positions for that market view under the
session's risk limits. Each position is measured against its instrument's
**risk limit**. The Risk limits tab explains where this number comes from. This
allows different instruments to be compared on the same scale.

### The six components

- **Overall portfolio fit — 35 points:** Does the complete pattern of positions
  resemble the optimal portfolio?
- **Position direction — 20 points:** Is each position long, short, or flat on
  the correct side?
- **Position size — 20 points:** Is the amount of risk close to the optimal
  amount?
- **Option choice — 10 points:** Were options used when useful, avoided when
  unnecessary, and sized appropriately?
- **Own-view consistency — 7.5 points:** Do the positions agree with the user's
  conviction sliders?
- **Limit discipline — 7.5 points:** Did the portfolio stay within the
  session's risk limits, including the limit on how much it could lose in a
  fixed adverse market test?

Together, the components make $100$ points:

$$
35+20+20+10+7.5+7.5=100.
$$

The assessment calculates Pillar 2 separately for each period, then takes the simple
average of those period scores. Profit and loss is shown for context but does
not directly add or remove Pillar 2 points.

Pillar 2 can only be scored when the scenario has a complete optimal portfolio
for comparison.
`;

const PillarTwoOverviewGuide: React.FC = () => (
  <aside
    aria-labelledby="pillar-two-overview-title"
    className="assessment-inspect-assessment__pillar-one-guide"
  >
    <header className="assessment-inspect-assessment__pillar-one-guide-header">
      <div className="assessment-inspect-assessment__pillar-one-guide-kicker">
        <span>
          <InfoCircleOutlined aria-hidden="true" /> Scoring guide
        </span>
        <Tag color="blue">100 points</Tag>
      </div>
      <Typography.Title id="pillar-two-overview-title" level={4}>
        Pillar 2 overview
      </Typography.Title>
      <Typography.Text type="secondary">
        How portfolio expression becomes a score for each period.
      </Typography.Text>
    </header>

    <div className="assessment-inspect-assessment__pillar-one-guide-body">
      <ReactMarkdown rehypePlugins={[rehypeKatex]} remarkPlugins={[remarkMath]}>
        {PILLAR_TWO_OVERVIEW}
      </ReactMarkdown>
    </div>
  </aside>
);

export default PillarTwoOverviewGuide;
